import { useMemo, useState } from "react";
import { TrendUp, Clock, CurrencyDollar, ArrowRight } from "@phosphor-icons/react";
import { useI18n } from "@/i18n";
import { FX } from "@/lib/fx";

const compact = (n, symbol) => {
  if (n >= 1_000_000) return `${symbol}${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1000) return `${symbol}${Math.round(n / 1000)}k`;
  return `${symbol}${Math.round(n)}`;
};
const money = (n, symbol) => `${symbol}${Math.round(n).toLocaleString()}`;

export const RoiCalculator = ({ onCta, currency = "usd", plans = [] }) => {
  const { t } = useI18n();
  const [lcs, setLcs] = useState(10);
  const [value, setValue] = useState(50000); // internal USD

  const pro = plans.find((p) => p.highlight && p.cadence === "month");
  const fx = FX[currency] || FX.usd;
  const symbol = fx.symbol;
  const rate = fx.rate;
  const proUsd = pro?.prices?.usd?.amount ?? 199;
  // HKD uses the real Stripe price; USD/EUR/GBP derive from the USD plan price.
  const priceCur = currency === "hkd" ? (pro?.prices?.hkd?.amount ?? 1500) : Math.round(proUsd * rate);

  const { annualRisk, hoursSaved, planUsd, roi } = useMemo(() => {
    const perPresentation = 0.15 * (100 + value * 0.08 * 15 / 365);
    const annualRiskUsd = lcs * 12 * perPresentation;
    const annualRisk = annualRiskUsd * rate;
    const hoursSaved = lcs * 1.5;
    const planUsd = lcs <= 30 ? Math.min(lcs * 15, 199) : 599;
    const roi = annualRiskUsd / (planUsd * 12);
    return { annualRisk, hoursSaved, planUsd, roi };
  }, [lcs, value, rate]);

  return (
    <section className="max-w-7xl mx-auto px-6 py-20" data-testid="roi-calculator">
      <div className="text-center">
        <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-lc-sky">{t("roi.overline")}</span>
        <h2 className="mt-3 font-serif text-3xl sm:text-4xl tracking-tight">{t("roi.title")}</h2>
      </div>

      <div className="mt-12 grid lg:grid-cols-2 gap-6">
        {/* controls */}
        <div className="rounded-sm border border-white/10 bg-lc-slate/30 p-8 space-y-10">
          <div>
            <div className="flex items-center justify-between">
              <label className="text-sm font-semibold text-white">{t("roi.lcsLabel")}</label>
              <span data-testid="roi-lcs-value" className="font-mono text-lg text-lc-sky">{lcs}</span>
            </div>
            <input data-testid="roi-lcs-slider" type="range" min={1} max={50} value={lcs}
              onChange={(e) => setLcs(+e.target.value)}
              className="mt-4 w-full accent-lc-sky cursor-pointer" />
            <div className="mt-1 flex justify-between font-mono text-[10px] text-lc-fog"><span>1</span><span>50</span></div>
          </div>
          <div>
            <div className="flex items-center justify-between">
              <label className="text-sm font-semibold text-white">{t("roi.valLabel")} ({currency.toUpperCase()})</label>
              <span data-testid="roi-value-value" className="font-mono text-lg text-lc-sky">{money(value * rate, symbol)}</span>
            </div>
            <input data-testid="roi-value-slider" type="range" min={10000} max={500000} step={5000} value={value}
              onChange={(e) => setValue(+e.target.value)}
              className="mt-4 w-full accent-lc-sky cursor-pointer" />
            <div className="mt-1 flex justify-between font-mono text-[10px] text-lc-fog"><span>{compact(10000 * rate, symbol)}</span><span>{compact(500000 * rate, symbol)}</span></div>
          </div>
        </div>

        {/* results */}
        <div className="rounded-sm border border-lc-sky/30 bg-lc-navy p-8 flex flex-col">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="rounded-sm bg-lc-slate/40 p-5">
              <TrendUp weight="duotone" size={22} className="text-lc-ok" />
              <div data-testid="roi-annual-risk" className="mt-3 font-serif text-3xl text-white">{money(annualRisk, symbol)}</div>
              <p className="mt-1 text-[11px] leading-snug text-lc-fog">{t("roi.riskLabel")}</p>
            </div>
            <div className="rounded-sm bg-lc-slate/40 p-5">
              <Clock weight="duotone" size={22} className="text-lc-sky" />
              <div data-testid="roi-hours" className="mt-3 font-serif text-3xl text-white">{hoursSaved.toFixed(1)}<span className="text-lg text-lc-fog"> {t("roi.hrs")}</span></div>
              <p className="mt-1 text-[11px] leading-snug text-lc-fog">{t("roi.hoursLabel")}</p>
            </div>
            <div className="rounded-sm bg-lc-slate/40 p-5">
              <CurrencyDollar weight="duotone" size={22} className="text-lc-fog" />
              <div data-testid="roi-investment" className="mt-3 font-serif text-3xl text-white">{money(planUsd * rate, symbol)}<span className="text-lg text-lc-fog">{t("roi.perMo")}</span></div>
              <p className="mt-1 text-[11px] leading-snug text-lc-fog">{lcs > 30 ? "LC Shield Firm plan" : (lcs * 15 < 199 ? `Estimated LC Shield cost (Single Pass × ${lcs} audit${lcs === 1 ? "" : "s"})` : "LC Shield Professional plan")}</p>
            </div>
            <div className="rounded-sm border border-lc-ok/40 bg-lc-ok/10 p-5 flex flex-col justify-center">
              <span className="font-mono text-[10px] uppercase tracking-widest text-lc-ok">{t("roi.roiLabel")}</span>
              <div data-testid="roi-badge" className="mt-1 font-serif text-4xl font-semibold text-lc-ok">
                ~{Math.max(1, Math.round(roi))}x
              </div>
            </div>
          </div>
          <button data-testid="roi-cta-btn" onClick={onCta}
            className="group mt-6 inline-flex items-center justify-center gap-2 rounded-sm bg-lc-sky px-5 py-3 text-sm font-semibold text-lc-navy hover:bg-white transition-colors duration-200">
            {t("roi.cta")}
            <ArrowRight weight="bold" size={15} className="group-hover:translate-x-1 transition-transform duration-200" />
          </button>
          <p className="mt-3 text-center text-[10px] text-lc-fog">Illustrative model, not ICC data: 15% of presentations refused on first presentation, USD 100 bank fee, 15-day delay costed at 8% p.a. funding, 1.5 hours of staff time per LC, and every discrepancy caught before presentation. Plan cost is the cheaper of Single Pass × LCs and the Professional plan (Firm above 30 LCs/month). Not a guarantee.</p>
        </div>
      </div>
    </section>
  );
};

export default RoiCalculator;
